import api from './api';

export type PlannerRouteStatus = 'DRAFT' | 'GENERATED' | 'MANUAL_EDITED' | 'LOCKED' | 'CONFIRMED';

export type PlannerRouteStop = {
  id: string;
  sequence: number;
  locationCode: string;
  arrivalTimeLocal: string | null;
  departureTimeLocal: string | null;
  boardingPax: number;
  droppingPax: number;
  onboardAfterPax: number;
  notes?: string | null;
  isManual?: boolean;
  warningFlags?: any;
};

export type PlannerRouteSectorLink = {
  id: string;
  routeId: string;
  fromStopId?: string | null;
  toStopId?: string | null;
  bookingId?: string | null;
  missionId?: string | null;
  sectorKey: string;
  linkRole?: string | null;
  isSyntheticSector?: boolean;
  paxCount?: number;
  etdLocal?: string | null;
  etaLocal?: string | null;
  metadata?: any;
};

export type PlannerRoute = {
  id: string;
  operationalDate: string;
  tailNumber: string;
  status: PlannerRouteStatus;
  summaryPath?: string | null;
  maxOnboardPax: number;
  warnings?: any;
  diagnostics?: any;
  sourceSnapshot?: any;
  stops: PlannerRouteStop[];
  sectorLinks: PlannerRouteSectorLink[];
};

export type PlannerRouteSectorInput = {
  sectorKey: string;
  tailNumber: string;
  from: string;
  to: string;
  pax: number;
  etd?: string;
  eta?: string;
  bookingId?: string;
  missionId?: string;
  isSynthetic?: boolean;
};

export type PlannerRouteStopUpdateInput = {
  id?: string;
  sequence: number;
  locationCode: string;
  arrivalTimeLocal?: string;
  departureTimeLocal?: string;
  boardingPax: number;
  droppingPax: number;
  onboardAfterPax?: number;
  notes?: string;
  isManual?: boolean;
  warningFlags?: any;
};

export const plannerService = {
  async getScheduledAssignments(date: string): Promise<Record<string, string>> {
    const res = await api.get('/planner/scheduled-assignments', { params: { date } });
    return (res.data?.data?.assignments || {}) as Record<string, string>;
  },

  async saveScheduledAssignments(date: string, assignments: Record<string, string>): Promise<Record<string, string>> {
    const res = await api.put('/planner/scheduled-assignments', { date, assignments });
    return (res.data?.data?.assignments || {}) as Record<string, string>;
  },

  async getScheduledCrewAssignments(date: string): Promise<Record<string, string[]>> {
    const res = await api.get('/planner/scheduled-crew-assignments', { params: { date } });
    return (res.data?.data?.assignments || {}) as Record<string, string[]>;
  },

  async saveScheduledCrewAssignments(date: string, assignments: Record<string, string[]>): Promise<Record<string, string[]>> {
    const res = await api.put('/planner/scheduled-crew-assignments', { date, assignments });
    return (res.data?.data?.assignments || {}) as Record<string, string[]>;
  },

  async getPlannerRoutes(date: string): Promise<PlannerRoute[]> {
    const res = await api.get('/planner/routes', { params: { date } });
    return (res.data?.data?.routes || []) as PlannerRoute[];
  },

  async generatePlannerRoute(payload: {
    date: string;
    tailNumber?: string;
    sectors: PlannerRouteSectorInput[];
    forceRegenerate?: boolean;
  }): Promise<{ routes: PlannerRoute[]; skipped: Array<{ tailNumber: string; reason: string }> }> {
    const res = await api.post('/planner/routes/generate', payload);
    return {
      routes: (res.data?.data?.routes || []) as PlannerRoute[],
      skipped: (res.data?.data?.skipped || []) as Array<{ tailNumber: string; reason: string }>,
    };
  },

  async updatePlannerRoute(
    routeId: string,
    payload: {
      stops: PlannerRouteStopUpdateInput[];
      warnings?: any;
      diagnostics?: any;
      summaryPath?: string;
      maxOnboardPax?: number;
    }
  ): Promise<PlannerRoute | null> {
    const res = await api.put(`/planner/routes/${routeId}`, payload);
    return (res.data?.data?.route || null) as PlannerRoute | null;
  },

  async lockPlannerRoute(routeId: string): Promise<PlannerRoute | null> {
    const res = await api.post(`/planner/routes/${routeId}/lock`);
    return (res.data?.data?.route || null) as PlannerRoute | null;
  },

  async confirmPlannerRoute(routeId: string): Promise<PlannerRoute | null> {
    const res = await api.post(`/planner/routes/${routeId}/confirm`);
    return (res.data?.data?.route || null) as PlannerRoute | null;
  },

  async resetPlannerRoute(routeId: string): Promise<PlannerRoute | null> {
    const res = await api.post(`/planner/routes/${routeId}/reset`);
    return (res.data?.data?.route || null) as PlannerRoute | null;
  },

  async getPlannerRouteSheetHtml(routeId: string): Promise<string> {
    const res = await api.get(`/planner/routes/${routeId}/sheet/html`, {
      responseType: 'text',
      headers: {
        Accept: 'text/html',
      },
    });
    return String(res.data || '');
  },
};
